import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";
import { TenantInfo } from "../models/tenant.model";
import { TenantSwitchingState, initialTenantSwitchingState } from "./tenant-switching.state";
import { 
  TenantSwitchingActions, 
  setAvailableTenants, 
  setSelectedTenant, 
  setTenantSwitchingLoading, 
  setError, 
  clearError, 
  setSwitching, 
  clearTenants 
} from "./tenant-switching.actions";
import { tenantSwitchingReducer } from "./tenant-switching.reducer";
import { 
  selectAvailableTenants, 
  selectSelectedTenantId, 
  selectSelectedTenant, 
  selectTenantSwitchingLoading, 
  selectTenantSwitchingError, 
  selectIsSwitching,
  selectTenantOptions,
  selectHasMultipleTenants,
  selectCanSwitchTenants
} from "./tenant-switching.selectors";

@Injectable({
  providedIn: "root"
})
export class TenantSwitchingStore {
  private state$ = new BehaviorSubject<TenantSwitchingState>(initialTenantSwitchingState);

  // Public observables
  public availableTenants$ = this.state$.asObservable().pipe(
    map(state => selectAvailableTenants(state))
  );

  public selectedTenantId$ = this.state$.asObservable().pipe(
    map(state => selectSelectedTenantId(state))
  );

  public selectedTenant$ = this.state$.asObservable().pipe(
    map(state => selectSelectedTenant(state))
  );

  public loading$ = this.state$.asObservable().pipe(
    map(state => selectTenantSwitchingLoading(state))
  );

  public error$ = this.state$.asObservable().pipe(
    map(state => selectTenantSwitchingError(state))
  );

  public isSwitching$ = this.state$.asObservable().pipe(
    map(state => selectIsSwitching(state))
  );

  public tenantOptions$ = this.state$.asObservable().pipe(
    map(state => selectTenantOptions(state))
  );

  public hasMultipleTenants$ = this.state$.asObservable().pipe(
    map(state => selectHasMultipleTenants(state))
  );

  public canSwitchTenants$ = this.state$.asObservable().pipe(
    map(state => selectCanSwitchTenants(state))
  );

  // Getters for current state
  get currentState(): TenantSwitchingState {
    return this.state$.getValue();
  }

  get availableTenants(): TenantInfo[] {
    return selectAvailableTenants(this.currentState);
  }

  get selectedTenantId(): string | null {
    return selectSelectedTenantId(this.currentState);
  }

  get selectedTenant(): TenantInfo | null {
    return selectSelectedTenant(this.currentState);
  }

  get hasMultipleTenants(): boolean {
    return selectHasMultipleTenants(this.currentState);
  }

  get canSwitchTenants(): boolean {
    return selectCanSwitchTenants(this.currentState);
  }

  // Action dispatchers
  dispatchSetAvailableTenants(tenants: TenantInfo[]): void {
    this.dispatch(setAvailableTenants(tenants));
  }

  dispatchSetSelectedTenant(tenantId: string | null): void {
    this.dispatch(setSelectedTenant(tenantId));
  }

  dispatchSetLoading(loading: boolean): void {
    this.dispatch(setTenantSwitchingLoading(loading));
  }

  dispatchSetError(error: string): void {
    this.dispatch(setError(error));
  }

  dispatchClearError(): void {
    this.dispatch(clearError());
  }

  dispatchSetSwitching(switching: boolean): void {
    this.dispatch(setSwitching(switching));
  }

  dispatchClearTenants(): void {
    this.dispatch(clearTenants());
  }

  // Private method to dispatch actions
  private dispatch(action: TenantSwitchingActions): void {
    const currentState = this.state$.getValue();
    const newState = tenantSwitchingReducer(currentState, action);
    this.state$.next(newState);
  }
}
